import { describeError } from '@/lib/form/errorMessages';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Permission,
  catalogCountertopAttributes,
  type CatalogCountertopAttributes,
  type CatalogItemDetails,
  type CreateCatalogItemRequest,
  type EdgeListItem,
  type MaterialListItem,
  type PreviewCountertopRequest,
  type UpdateCatalogItemRequest,
} from '@kreart/shared';
import { Button } from '@/components/ui/button';
import { Form, TextField, CheckboxField, FormActions, FormErrorBanner } from '@/components/form';
import { positiveIntegerFieldProps } from '@/components/form/numberInputProps';
import { useAuth } from '@/features/auth/useAuth';
import {
  useCatalogItem,
  useCreateCatalogItem,
  useDeleteCatalogItem,
  useUpdateCatalogItem,
} from '@/features/catalog/hooks';
import { errorKeyFor } from '@/features/catalog/errorMessages';
import { CatalogDangerZone } from '@/features/catalog/components/CatalogDangerZone';
import {
  EdgeAutosuggest,
  MaterialAutosuggest,
} from '@/features/project-items/shared/pickers/CatalogAutosuggest';
import { BreakdownPanel } from '@/features/pricing/components/BreakdownPanel';
import { usePreviewCountertop } from '@/features/pricing/hooks';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { edgesApi } from '@/features/catalog/components/admin/api';
import { chooseEdgeForMaterial } from '@/features/project-items/shared/edgeAutoSelect';
import { useDefaultMaterialId } from '@/features/project-items/shared/useDefaultMaterial';

const DEFAULT_COUNTERTOP_WIDTH_MM = 600;
const DEFAULT_COUNTERTOP_DEPTH_MM = 560;
const DEFAULT_COUNTERTOP_THICKNESS_MM = 30;

const decimalRegex = /^\d+(\.\d+)?$/;
const decimalString = z.string().regex(decimalRegex);
const positiveIntegerString = z
  .string()
  .regex(/^\d+$/, 'Expected positive integer')
  .refine((v) => parseInt(v, 10) > 0, { message: 'Must be greater than 0' });
const requiredCatalogId = z.string().min(1, 'Choose a value from the suggestions');

const formSchema = z.object({
  name: z.string().trim().min(1).max(200),
  widthMm: positiveIntegerString,
  depthMm: positiveIntegerString,
  materialId: requiredCatalogId,
  edgeId: z.string(),
  material: z.string().trim().max(100).optional(),
  edgeProfile: z.string().trim().max(100).optional(),
  doubled: z.boolean(),
  unitPriceNet: decimalString,
});
type FormValues = z.infer<typeof formSchema>;

function defaults(defaultMaterialId?: string): FormValues {
  return {
    name: '',
    widthMm: String(DEFAULT_COUNTERTOP_WIDTH_MM),
    depthMm: String(DEFAULT_COUNTERTOP_DEPTH_MM),
    materialId: defaultMaterialId ?? '',
    edgeId: '',
    material: '',
    edgeProfile: '',
    doubled: false,
    unitPriceNet: '0.00',
  };
}

function parseAttributes(item: CatalogItemDetails | undefined): CatalogCountertopAttributes {
  if (!item?.attributes) return {};
  const parsed = catalogCountertopAttributes.safeParse(item.attributes);
  return parsed.success ? parsed.data : {};
}

function valuesFromItem(item: CatalogItemDetails): FormValues {
  const attrs = parseAttributes(item);
  return {
    ...defaults(),
    name: item.name,
    widthMm: String(item.defaultWidthMm || DEFAULT_COUNTERTOP_WIDTH_MM),
    depthMm: String(item.defaultDepthMm || DEFAULT_COUNTERTOP_DEPTH_MM),
    materialId: attrs.materialId ?? '',
    edgeId: attrs.edgeId ?? '',
    material: attrs.material ?? item.material ?? '',
    edgeProfile: attrs.edgeProfile ?? '',
    doubled: attrs.doubled ?? false,
    unitPriceNet: item.defaultCostNet ?? '0.00',
  };
}

function buildBody(
  values: FormValues,
  mode: 'create' | 'edit',
): CreateCatalogItemRequest | UpdateCatalogItemRequest {
  const widthMm = parseInt(values.widthMm, 10);
  const depthMm = parseInt(values.depthMm, 10);
  const attributes: CatalogCountertopAttributes = {
    materialId: values.materialId || null,
    edgeId: values.edgeId || null,
    material: values.material || undefined,
    edgeProfile: values.edgeProfile || undefined,
    doubled: values.doubled,
    pricingMode: 'AUTO',
  };
  const body = {
    name: values.name,
    defaultWidthMm: widthMm,
    defaultHeightMm: null,
    defaultDepthMm: depthMm,
    thicknessMm: DEFAULT_COUNTERTOP_THICKNESS_MM,
    material: values.material || undefined,
    defaultCostNet: values.unitPriceNet,
    currency: 'RON' as const,
    attributes: attributes as Record<string, unknown>,
  };
  return mode === 'create' ? { type: 'COUNTERTOP' as const, ...body } : body;
}

function parsePositiveIntegerValue(value: string | undefined): number | null {
  if (!value || !/^\d+$/.test(value)) return null;
  const parsed = parseInt(value, 10);
  return parsed > 0 ? parsed : null;
}

interface Props {
  mode: 'create' | 'edit';
}

export function CatalogCountertopFormPage({ mode }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { id?: string };
  const id = mode === 'edit' ? params.id : undefined;
  const { user } = useAuth();
  const can = (p: string) => user?.permissions.includes(p) ?? false;
  const canUpdate = can(Permission.CATALOG_UPDATE);
  const canDelete = can(Permission.CATALOG_DELETE);
  const defaultMaterialId = useDefaultMaterialId();

  const itemQuery = useCatalogItem(id);
  const createMut = useCreateCatalogItem();
  const updateMut = useUpdateCatalogItem(id ?? '');
  const deleteMut = useDeleteCatalogItem(id ?? '');
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialListItem | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaults(defaultMaterialId),
  });

  useEffect(() => {
    if (mode !== 'edit' || !itemQuery.data) return;
    form.reset(valuesFromItem(itemQuery.data));
    setSelectedMaterial(null);
  }, [form, itemQuery.data, mode]);

  // Fill default material once it resolves (async), only for new items.
  useEffect(() => {
    if (mode !== 'create' || !defaultMaterialId) return;
    const current = form.getValues('materialId');
    if (!current) {
      form.setValue('materialId', defaultMaterialId);
    }
  }, [defaultMaterialId, form, mode]);

  const edgeSearch = selectedMaterial?.manufacturer?.trim() ?? '';
  const edgesQuery = useQuery({
    queryKey: ['catalog-edges', 'countertop-auto-select', edgeSearch],
    queryFn: () =>
      edgesApi
        .list({
          page: 1,
          pageSize: 100,
          active: true,
          includeDeleted: false,
          search: edgeSearch,
        })
        .then((res) => res.items),
    enabled:
      Boolean(selectedMaterial) &&
      selectedMaterial?.category !== 'MDF_VOPSIT' &&
      Boolean(edgeSearch),
    meta: { silent: true },
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!selectedMaterial) return;
    if (selectedMaterial.category === 'MDF_VOPSIT' || !edgeSearch) {
      form.setValue('edgeId', '', { shouldDirty: true, shouldValidate: true });
      form.setValue('edgeProfile', '', { shouldDirty: true });
      form.clearErrors('edgeId');
      return;
    }
    if (!edgesQuery.data) return;
    const edge = chooseEdgeForMaterial(selectedMaterial, edgesQuery.data);
    form.setValue('edgeId', edge?.id ?? '', { shouldDirty: true, shouldValidate: true });
    form.setValue('edgeProfile', edge?.name ?? '', { shouldDirty: true });
    form.clearErrors('edgeId');
  }, [edgeSearch, edgesQuery.data, form, selectedMaterial]);

  const watched = useDebouncedValue(form.watch(), 300);
  const previewBody: PreviewCountertopRequest | null = useMemo(() => {
    const widthMm = parsePositiveIntegerValue(watched.widthMm);
    const depthMm = parsePositiveIntegerValue(watched.depthMm);
    if (!watched.materialId || widthMm === null || depthMm === null) {
      return null;
    }
    return {
      widthMm,
      depthMm,
      materialId: watched.materialId,
      edgeId: watched.edgeId || null,
      doubled: watched.doubled,
      quantity: '1',
      coefficient: '1',
    };
  }, [watched]);
  const preview = usePreviewCountertop(previewBody);

  useEffect(() => {
    if (preview.data) {
      const subtotal = parseFloat(preview.data.breakdown.subtotalNet);
      if (Number.isFinite(subtotal) && subtotal >= 0) {
        form.setValue('unitPriceNet', subtotal.toFixed(2), { shouldDirty: false });
      }
    }
  }, [form, preview.data]);

  const isReadOnly = mode === 'edit' && !canUpdate;
  const submitting = mode === 'create' ? createMut.isPending : updateMut.isPending;
  const submitError = mode === 'create' ? createMut.error : updateMut.error;

  const onMaterialSelected = (item: MaterialListItem | null) => {
    setSelectedMaterial(item);
    form.setValue('material', item?.name ?? '', { shouldDirty: true });
    form.setValue('edgeId', '', { shouldDirty: true, shouldValidate: true });
    form.setValue('edgeProfile', '', { shouldDirty: true });
  };

  const onEdgeSelected = (item: EdgeListItem | null) => {
    form.setValue('edgeProfile', item?.name ?? '', { shouldDirty: true });
  };

  const onSubmit = async (values: FormValues) => {
    try {
      if (mode === 'create') {
        await createMut.mutateAsync(buildBody(values, mode) as CreateCatalogItemRequest);
        void navigate({ to: '/countertops', search: {} });
        return;
      }
      if (!id) return;
      await updateMut.mutateAsync(buildBody(values, mode) as UpdateCatalogItemRequest);
    } catch {
      /* surfaced via mutation errors */
    }
  };

  const onDelete = async () => {
    if (!id) return;
    try {
      await deleteMut.mutateAsync();
      void navigate({ to: '/countertops', search: {} });
    } catch {
      /* surfaced via deleteMut.error */
    }
  };

  if (mode === 'edit' && itemQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  }
  if (mode === 'edit' && itemQuery.isError) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        {describeError(t, itemQuery.error, errorKeyFor)}
      </div>
    );
  }

  const titleKey = mode === 'create' ? 'countertops.create.title' : 'countertops.edit.title';

  return (
    <div className="w-full space-y-4">
      <Link
        to="/countertops"
        search={{}}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> {t('countertops.backToList')}
      </Link>
      <h1 className="text-2xl font-semibold text-foreground">{t(titleKey)}</h1>

      <Form
        form={form}
        onSubmit={onSubmit}
        className="space-y-4 rounded-md border border-border bg-card p-4"
      >
        <fieldset disabled={isReadOnly} className="space-y-4">
          <TextField name="name" label={t('projectItems.countertops.fields.name')} autoFocus />
          <div className="grid grid-cols-2 gap-3">
            <TextField
              name="widthMm"
              label={t('projectItems.countertops.fields.lengthMm')}
              {...positiveIntegerFieldProps}
            />
            <TextField
              name="depthMm"
              label={t('projectItems.countertops.fields.widthMm')}
              {...positiveIntegerFieldProps}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MaterialAutosuggest
              name="materialId"
              label={t('projectItems.countertops.fields.material')}
              required
              onSelectedOption={onMaterialSelected}
            />
            <EdgeAutosuggest
              name="edgeId"
              label={t('projectItems.countertops.fields.edgeId')}
              emptyOptionLabel={t('pricing.noEdge')}
              onSelectedOption={onEdgeSelected}
            />
          </div>
          <CheckboxField name="doubled" label={t('countertops.fields.doubled')} pill />
          <div className="rounded-md border border-border p-3 space-y-3">
            <BreakdownPanel
              breakdown={preview.data?.breakdown ?? null}
              loading={preview.isFetching}
              error={preview.error}
            />
          </div>
        </fieldset>

        <FormErrorBanner error={submitError} resolveKey={errorKeyFor} />

        {!isReadOnly && (
          <FormActions
            submitLabel={t(mode === 'create' ? 'countertops.create.submit' : 'common.save')}
            isSubmitting={submitting}
            submitDisabled={!form.formState.isDirty && mode === 'edit'}
          >
            <Link to="/countertops" search={{}}>
              <Button type="button" variant="ghost">
                {t('common.cancel')}
              </Button>
            </Link>
          </FormActions>
        )}
      </Form>

      {mode === 'edit' && canDelete && itemQuery.data && (
        <CatalogDangerZone
          itemName={itemQuery.data.name}
          pending={deleteMut.isPending}
          error={deleteMut.error}
          onConfirm={onDelete}
        />
      )}
    </div>
  );
}
